import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';
import { ThunkAPI } from '../../../rootReducer';
import { basicApiAsyncThunkCreator } from '../../../../services/apiAsyncThunkCreator';
import { ContentTypes, CORE_SERVICES, HttpMethods, SERVICES } from '../../../../services/api';
import { showErrorMessage } from '../../../notifications';
import { Regions } from '../../../../components/common/domain/accountRegions';
import {
  IFetchCurrentTermPayload,
  IFetchCurrentTermResponse,
  IFetchProjectVideoData,
  IFetchProjectVideoDataPayload,
  IFetchProjectVideoDataResponse,
  IFetchProjectVideoUsage,
  IFetchProjectVideoUsageData,
  IFetchProjectVideoUsagePayload
} from './interfaces';
import { parseError } from '../../../../utils/utils';

export const VIDEO_TRACKING_PREFIX = 'video';

const getRegionSuffix = (region: Regions) => {
  switch (region) {
  case Regions.Australia:
    return '_AU';
  case Regions.EuropeUnion:
    return '_EUROPE';
  case Regions.Global:
  default:
    return '_GLOBAL';
  }
};

// fetchAccountCurrentTerm, fetchProjectVideoUsage, fetchProjectVideoData should be used together to fetch all needed data for account video page

// Step 1
export const fetchAccountCurrentTerm: AsyncThunk<IFetchCurrentTermResponse, IFetchCurrentTermPayload, ThunkAPI> =
  createAsyncThunk(
    `${VIDEO_TRACKING_PREFIX}/currentTerm`,
    async (data, thunk) => {
      try {
        const { accountId, region } = data;
        const response = await basicApiAsyncThunkCreator<IFetchCurrentTermResponse, undefined>({
          endpoint: `/usage/current-term/${accountId}`,
          microservice: CORE_SERVICES.VIDEO_TRACKING_SERVICE,
          authenticated: true,
          method: HttpMethods.Get,
          contentType: ContentTypes.ApplicationJson
        })(undefined, thunk);

        if (!('termStartDate' in response)) {
          return thunk.rejectWithValue('No current term');
        }

        const { termStartDate } = response;
        thunk.dispatch(fetchProjectVideoUsage({
          termStartDate,
          accountId,
          region
        }));
        return response;
      } catch (err) {
        const errorMessage = parseError(err, 'Could not fetch account current term');
        thunk.dispatch(showErrorMessage(errorMessage));
        return thunk.rejectWithValue(err);
      }
    }
  );

// Step 2
export const fetchProjectVideoUsage: AsyncThunk<undefined, IFetchProjectVideoUsagePayload, ThunkAPI> =
  createAsyncThunk(
    `${VIDEO_TRACKING_PREFIX}/projectVideoUsage`,
    async (data, thunk) => {
      try {
        const { accountId: companyId, region, termStartDate } = data;
        const regionSuffix = getRegionSuffix(region);
        const response = await basicApiAsyncThunkCreator<IFetchProjectVideoUsage[], IFetchProjectVideoUsageData>({
          endpoint: '/tracking/usage',
          microservice: SERVICES[`VIDEO_ANALYSIS_SERVICE${regionSuffix}`],
          authenticated: true,
          method: HttpMethods.Post,
          contentType: ContentTypes.ApplicationJson
        })({
          termStartDate,
          companyId
        }, thunk);

        if (!Array.isArray(response)) {
          throw new Error('Could not fetch the project video usage');
        }

        thunk.dispatch(fetchProjectVideoData({
          accountId: companyId,
          region,
          projectVideoUsages: response
        }));
        return;
      } catch (err) {
        const errorMessage = parseError(err, 'Could not fetch the project video usage');
        thunk.dispatch(showErrorMessage(errorMessage));
        return thunk.rejectWithValue(err);
      }
    }
  );

// Step 3
export const fetchProjectVideoData: AsyncThunk<IFetchProjectVideoDataResponse, IFetchProjectVideoDataPayload, ThunkAPI> =
  createAsyncThunk(
    `${VIDEO_TRACKING_PREFIX}/projectVideoData`,
    async (data, thunk) => {
      try {
        const { accountId: companyId, projectVideoUsages, region } = data;
        if (projectVideoUsages.length === 0) {
          return [];
        }
        const regionSuffix = getRegionSuffix(region);
        const projectIds = projectVideoUsages.map((project) => project.projectId);
        const response = await basicApiAsyncThunkCreator<IFetchProjectVideoDataResponse, IFetchProjectVideoData>({
          endpoint: '/stats/info',
          microservice: SERVICES[`PROJECTS_SERVICE_V2${regionSuffix}`],
          authenticated: true,
          method: HttpMethods.Post,
          contentType: ContentTypes.ApplicationJson
        })({
          companyId,
          projectIds
        }, thunk);

        if (!Array.isArray(response)) {
          throw new Error('Could not fetch the project data');
        }

        const projectVideoUsage = keyBy(projectVideoUsages, 'projectId');
        const projectVideoData = keyBy(response, 'projectId');
        return Object.keys(projectVideoUsage).map((projectId) => {
          return {
            ...projectVideoUsage[projectId],
            ...projectVideoData[projectId]
          };
        });
      } catch (err) {
        const errorMessage = parseError(err);
        thunk.dispatch(showErrorMessage(errorMessage));
        return thunk.rejectWithValue(err);
      }
    }
  );
